import {
  comparisonResponseSchema,
  drilldownResponseSchema,
  exactVideoDetailResponseSchema,
  exactVideoTrendResponseSchema,
  materializeReportFilterQuery,
  replaceReportPreferencesRequestSchema,
  reportingErrorResponseSchema,
  reportPreferencesResponseSchema,
  trendResponseSchema,
  videoComparisonRequestSchema,
  videoSelectionSchema,
  videoReportResponseSchema,
  type ComparisonResponse,
  type DrilldownResponse,
  type ExactVideoDetailResponse,
  type ExactVideoTrendResponse,
  type ReportFilterStateV1,
  type ReportMetricId,
  type ReportPreferencesResponse,
  type ReportSort,
  type ReplaceReportPreferencesRequest,
  type ReportingErrorCode,
  type TrendResponse,
  type VideoReportResponse,
  type VideoSelection,
} from "@meta-video/contracts";

import {
  AccessDeniedError,
  ApiProtocolError,
  fetchWithCsrfRetry,
  isCsrfRejection,
  parseApiResponse,
  PROTECTED_QUERY_KEY,
  runtimeFetch,
  SessionExpiredError,
  type ApiRequestOptions,
} from "../../shared/api/client.js";

type Fetcher = typeof fetch;

export interface ReportRequest {
  readonly filters: ReportFilterStateV1;
  readonly limit: number;
  readonly offset: number;
  readonly sorts: readonly ReportSort[];
}

export interface TrendRequest {
  readonly filters: ReportFilterStateV1;
  readonly metric: ReportMetricId;
  readonly topN: 0 | 5 | 10 | 20;
}

export interface DrilldownRequest {
  readonly filters: ReportFilterStateV1;
  readonly selection: VideoSelection;
}

export interface ExactVideoRequest {
  readonly filters: ReportFilterStateV1;
  readonly selection: VideoSelection;
}

export interface ComparisonRequest {
  readonly filters: ReportFilterStateV1;
  readonly videos: readonly VideoSelection[];
}

export interface ReportApi {
  readonly getComparison: (
    input: ComparisonRequest,
    options?: ApiRequestOptions,
  ) => Promise<ComparisonResponse>;
  readonly getDrilldown: (
    input: DrilldownRequest,
    options?: ApiRequestOptions,
  ) => Promise<DrilldownResponse>;
  readonly getVideoDetail: (
    input: ExactVideoRequest,
    options?: ApiRequestOptions,
  ) => Promise<ExactVideoDetailResponse>;
  readonly getVideoSpendTrend: (
    input: ExactVideoRequest,
    options?: ApiRequestOptions,
  ) => Promise<ExactVideoTrendResponse>;
  readonly getPreferences: (
    options?: ApiRequestOptions,
  ) => Promise<ReportPreferencesResponse>;
  readonly getReport: (
    input: ReportRequest,
    options?: ApiRequestOptions,
  ) => Promise<VideoReportResponse>;
  readonly getTrend: (
    input: TrendRequest,
    options?: ApiRequestOptions,
  ) => Promise<TrendResponse>;
  readonly putPreferences: (
    input: ReplaceReportPreferencesRequest,
    options?: ApiRequestOptions,
  ) => Promise<ReportPreferencesResponse>;
}

export class ReportingApiError extends Error {
  constructor(
    readonly code: ReportingErrorCode,
    readonly statusCode: number,
    message: string,
  ) {
    super(message);
    this.name = "ReportingApiError";
  }
}

export function reportFilterSearchParams(
  filters: ReportFilterStateV1,
  now = new Date(),
): URLSearchParams {
  return materializeReportFilterQuery(filters, now);
}

export function exactVideoQueryKey(
  kind: "detail" | "spend",
  filterKey: string,
  selection: VideoSelection,
  api: ReportApi,
) {
  const exact = videoSelectionSchema.parse(selection);
  return [
    ...PROTECTED_QUERY_KEY,
    "report",
    "exact-video",
    kind,
    filterKey,
    exact.brandId,
    exact.videoId,
    api,
  ] as const;
}

function withQuery(path: string, params: URLSearchParams): string {
  const encoded = params.toString();
  return encoded === "" ? path : `${path}?${encoded}`;
}

function prepareExactVideoRequest(
  input: ExactVideoRequest,
  now: Date,
  invalidIdentityMessage: string,
): Readonly<{
  params: URLSearchParams;
  path: string;
  selection: VideoSelection;
}> {
  const selection = parseApiResponse(
    videoSelectionSchema,
    input.selection,
    invalidIdentityMessage,
  );
  return {
    params: reportFilterSearchParams(input.filters, now),
    path: `/api/reports/videos/${encodeURIComponent(
      selection.brandId,
    )}/${encodeURIComponent(selection.videoId)}`,
    selection,
  };
}

async function requestReportingJson(
  path: string,
  init: RequestInit,
  fetcher: Fetcher,
  withCsrf = false,
): Promise<unknown> {
  const response = withCsrf
    ? await fetchWithCsrfRetry(path, init, fetcher)
    : await fetcher(path, {
        ...init,
        credentials: "same-origin",
        headers: { accept: "application/json", ...init.headers },
      });
  if (response.status === 401) throw new SessionExpiredError();
  if (response.status === 403) {
    // A rejected request-verification token is a stale-token problem, not a
    // change of permissions. fetchWithCsrfRetry already retried once; treating
    // a second rejection as AccessDeniedError would report it to the router as
    // lost report access and clear every protected query.
    if (await isCsrfRejection(response)) {
      throw new ApiProtocolError(
        "Request verification failed. Retry the action.",
      );
    }
    throw new AccessDeniedError();
  }

  let value: unknown;
  try {
    value = await response.json();
  } catch {
    throw new ApiProtocolError(
      response.ok
        ? "Reporting response was not valid JSON"
        : `Reporting API request failed (${response.status})`,
    );
  }
  if (!response.ok) {
    const parsed = reportingErrorResponseSchema.safeParse(value);
    if (!parsed.success || parsed.data.statusCode !== response.status) {
      throw new ApiProtocolError(`Reporting API request failed (${response.status})`);
    }
    throw new ReportingApiError(
      parsed.data.code,
      parsed.data.statusCode,
      parsed.data.message,
    );
  }
  return value;
}

export function createReportApi(
  fetcher: Fetcher = runtimeFetch,
  now: () => Date = () => new Date(),
): ReportApi {
  return {
    async getComparison(input, options = {}) {
      const request = parseApiResponse(
        videoComparisonRequestSchema,
        { videos: input.videos },
        "Video comparison request has an invalid shape",
      );
      const params = reportFilterSearchParams(input.filters, now());
      params.set("videos", JSON.stringify(request.videos));
      const value = await requestReportingJson(
        withQuery("/api/reports/videos/comparison", params),
        { method: "GET", signal: options.signal },
        options.fetcher ?? fetcher,
      );
      return parseApiResponse(
        comparisonResponseSchema,
        value,
        "Video comparison response has an invalid shape",
      );
    },

    async getReport(input, options = {}) {
      const params = reportFilterSearchParams(input.filters, now());
      params.set("limit", String(input.limit));
      params.set("offset", String(input.offset));
      params.set(
        "sort",
        input.sorts.map(({ direction, metric }) => `${metric}:${direction}`).join(","),
      );
      const value = await requestReportingJson(
        withQuery("/api/reports/videos", params),
        { method: "GET", signal: options.signal },
        options.fetcher ?? fetcher,
      );
      return parseApiResponse(
        videoReportResponseSchema,
        value,
        "Video report response has an invalid shape",
      );
    },

    async getTrend(input, options = {}) {
      const params = reportFilterSearchParams(input.filters, now());
      params.set("metric", input.metric);
      params.set("topN", String(input.topN));
      const value = await requestReportingJson(
        withQuery("/api/reports/videos/trend", params),
        { method: "GET", signal: options.signal },
        options.fetcher ?? fetcher,
      );
      return parseApiResponse(
        trendResponseSchema,
        value,
        "Video trend response has an invalid shape",
      );
    },

    async getDrilldown(input, options = {}) {
      const params = reportFilterSearchParams(input.filters, now());
      const path = `/api/reports/videos/${encodeURIComponent(
        input.selection.brandId,
      )}/${encodeURIComponent(input.selection.videoId)}/ads`;
      const value = await requestReportingJson(
        withQuery(path, params),
        { method: "GET", signal: options.signal },
        options.fetcher ?? fetcher,
      );
      return parseApiResponse(
        drilldownResponseSchema,
        value,
        "Video drilldown response has an invalid shape",
      );
    },

    async getVideoDetail(input, options = {}) {
      const { params, path, selection } = prepareExactVideoRequest(
        input,
        now(),
        "Exact video detail request has an invalid identity",
      );
      const value = await requestReportingJson(
        withQuery(`${path}/detail`, params),
        { method: "GET", signal: options.signal },
        options.fetcher ?? fetcher,
      );
      const response = parseApiResponse(
        exactVideoDetailResponseSchema,
        value,
        "Exact video detail response has an invalid shape",
      );
      if (
        response.row !== null &&
        (response.row.brandId !== selection.brandId ||
          response.row.videoId !== selection.videoId)
      ) {
        throw new ApiProtocolError(
          "Exact video detail response has an invalid identity",
        );
      }
      return response;
    },

    async getVideoSpendTrend(input, options = {}) {
      const { params, path, selection } = prepareExactVideoRequest(
        input,
        now(),
        "Exact video spend request has an invalid identity",
      );
      const value = await requestReportingJson(
        withQuery(`${path}/trend`, params),
        { method: "GET", signal: options.signal },
        options.fetcher ?? fetcher,
      );
      const response = parseApiResponse(
        exactVideoTrendResponseSchema,
        value,
        "Exact video spend response has an invalid shape",
      );
      if (
        response.series.some(
          ({ brandId, videoId }) =>
            brandId !== selection.brandId || videoId !== selection.videoId,
        ) ||
        response.points.some(
          ({ brandId, videoId }) =>
            brandId !== selection.brandId || videoId !== selection.videoId,
        )
      ) {
        throw new ApiProtocolError(
          "Exact video spend response has an invalid identity",
        );
      }
      return response;
    },

    async getPreferences(options = {}) {
      const value = await requestReportingJson(
        "/api/report-preferences",
        { method: "GET", signal: options.signal },
        options.fetcher ?? fetcher,
      );
      return parseApiResponse(
        reportPreferencesResponseSchema,
        value,
        "Report preferences response has an invalid shape",
      );
    },

    async putPreferences(input, options = {}) {
      const body = parseApiResponse(
        replaceReportPreferencesRequestSchema,
        input,
        "Report preferences request has an invalid shape",
      );
      const requestFetcher = options.fetcher ?? fetcher;
      const value = await requestReportingJson(
        "/api/report-preferences",
        {
          body: JSON.stringify(body),
          headers: {
            "content-type": "application/json",
          },
          method: "PUT",
          signal: options.signal,
        },
        requestFetcher,
        true,
      );
      return parseApiResponse(
        reportPreferencesResponseSchema,
        value,
        "Report preferences response has an invalid shape",
      );
    },
  };
}

export const reportApi = createReportApi();
